// ---------------------------------------------------------------------------
// Effort class constants — mirrors ActivityClassificationService primary types
// ---------------------------------------------------------------------------
export const EFFORT_CLASSES = {
  // Trail tiers (priority order)
  ULTRA_ENDURANCE: "ultra_endurance",
  EXTREME_ULTRA:   "extreme_ultra",
  VK:              "vk",
  SKYRACE:         "skyrace",
  ULTRA_TRAIL:     "ultra_trail",
  LONG_TRAIL:      "long_trail",
  TRAIL_RUN:       "trail_run",
  SHORT_TRAIL:     "short_trail",

  // Road tiers (distance-only)
  EXTREME:         "extreme",
  ULTRA:           "ultra",
  MARATHON:        "marathon",
  LONG_RUN:        "long_run",
  HALF_MARATHON:   "half_marathon",
  MEDIUM:          "medium",
  TEN_K:           "ten_k",
  FIVE_K:          "five_k",
  SHORT:           "short",

  // Base run (non-named road tier default — replaces old EASY primaryType)
  BASE_RUN:  "base_run",

  // Intensity (applied to non-named road tiers as override)
  RECOVERY:  "recovery",
  EASY:      "easy",
  STEADY:    "steady",
  TEMPO:     "tempo",
  THRESHOLD: "threshold",
  INTERVALS: "intervals",
  HILLS:     "hills",
  SPEED:     "speed",
  FARTLEK:   "fartlek",
  RACE:      "race",
  UNKNOWN:   "unknown",
};

// Named road tiers: intensity step never overrides these
const NAMED_ROAD_TIERS = new Set([
  EFFORT_CLASSES.MARATHON,
  EFFORT_CLASSES.HALF_MARATHON,
  EFFORT_CLASSES.TEN_K,
  EFFORT_CLASSES.FIVE_K,
]);

const TRAIL_TYPE_CLASSES = new Set([
  EFFORT_CLASSES.ULTRA_ENDURANCE,
  EFFORT_CLASSES.EXTREME_ULTRA,
  EFFORT_CLASSES.VK,
  EFFORT_CLASSES.SKYRACE,
  EFFORT_CLASSES.ULTRA_TRAIL,
  EFFORT_CLASSES.LONG_TRAIL,
  EFFORT_CLASSES.TRAIL_RUN,
  EFFORT_CLASSES.SHORT_TRAIL,
]);

const BACKEND_PRIMARY_TYPE_TO_EFFORT_CLASS = {
  // Trail
  ULTRA_ENDURANCE: EFFORT_CLASSES.ULTRA_ENDURANCE,
  EXTREME_ULTRA:   EFFORT_CLASSES.EXTREME_ULTRA,
  VK:              EFFORT_CLASSES.VK,
  SKYRACE:         EFFORT_CLASSES.SKYRACE,
  ULTRA_TRAIL:     EFFORT_CLASSES.ULTRA_TRAIL,
  LONG_TRAIL:      EFFORT_CLASSES.LONG_TRAIL,
  TRAIL_RUN:       EFFORT_CLASSES.TRAIL_RUN,
  SHORT_TRAIL:     EFFORT_CLASSES.SHORT_TRAIL,
  // Road
  EXTREME:         EFFORT_CLASSES.EXTREME,
  ULTRA:           EFFORT_CLASSES.ULTRA,
  MARATHON:        EFFORT_CLASSES.MARATHON,
  LONG_RUN:        EFFORT_CLASSES.LONG_RUN,
  HALF_MARATHON:   EFFORT_CLASSES.HALF_MARATHON,
  MEDIUM:          EFFORT_CLASSES.MEDIUM,
  TEN_K:           EFFORT_CLASSES.TEN_K,
  FIVE_K:          EFFORT_CLASSES.FIVE_K,
  SHORT:           EFFORT_CLASSES.SHORT,
  // Intensity / other
  RECOVERY:        EFFORT_CLASSES.RECOVERY,
  BASE_RUN:        EFFORT_CLASSES.BASE_RUN,
  EASY:            EFFORT_CLASSES.BASE_RUN, // backward compat — old JSONB records before V54 migration
  STEADY:          EFFORT_CLASSES.STEADY,
  TEMPO:           EFFORT_CLASSES.TEMPO,
  THRESHOLD:       EFFORT_CLASSES.THRESHOLD,
  INTERVALS:       EFFORT_CLASSES.INTERVALS,
  HILLS:           EFFORT_CLASSES.HILLS,
  SPEED:           EFFORT_CLASSES.SPEED,
  FARTLEK:         EFFORT_CLASSES.FARTLEK,
  RACE:            EFFORT_CLASSES.RACE,
  UNKNOWN:         EFFORT_CLASSES.UNKNOWN,
};

// ---------------------------------------------------------------------------
// Trail tier thresholds (matches spec / ActivityClassificationService)
// ---------------------------------------------------------------------------
const T = {
  UE_MIN_KM:         150,
  UE_MIN_ELEV:       7500,
  UE_MIN_EPK:        40,

  VK_MAX_KM:         8,
  VK_MIN_ELEV:       1000,
  VK_MIN_EPK:        100,

  SKY_MIN_KM:        20,
  SKY_MAX_KM:        42,
  SKY_MIN_ELEV:      2000,
  SKY_MIN_EPK:       70,

  ULTRA_MIN_KM:      42,
  ULTRA_MTN_ELEV:    2500,
  ULTRA_MTN_EPK:     50,

  LONG_MIN_KM:       20,
  LONG_ELEV_M:       600,
  LONG_ELEV_MIN_KM:  12,
  LONG_COMB_KM:      15,
  LONG_COMB_ELEV:    400,

  TRAIL_MIN_KM:      8,
  TRAIL_GATE_ELEV:   100,
  TRAIL_GATE_EPK:    20,

  // Road tiers (distance-only, named windows at ±5%)
  ROAD_EXTREME_MIN:  100,
  ROAD_ULTRA_MIN:    44.3,   // exclusive lower: d > 44.3
  ROAD_MARA_MIN:     40.1,
  ROAD_MARA_MAX:     44.3,   // inclusive upper: d ≤ 44.3
  ROAD_LONG_MIN:     22.2,
  ROAD_LONG_MAX:     40.1,   // exclusive upper
  ROAD_HALF_MIN:     20.0,
  ROAD_HALF_MAX:     22.2,   // exclusive upper
  ROAD_MED_MIN:      10.5,
  ROAD_MED_MAX:      20.0,   // exclusive upper
  ROAD_10K_MIN:      9.5,
  ROAD_10K_MAX:      10.5,   // exclusive upper
  ROAD_5K_MIN:       4.75,
  ROAD_5K_MAX:       5.25,   // exclusive upper

  // Terrain m/km thresholds
  EPK_ROLLING:       10,
  EPK_HILLY:         25,
  EPK_MOUNTAIN:      50,

  FLAG_LOW_ELEV:     "low_elev_trail",
};

// ---------------------------------------------------------------------------
// Name-based keyword patterns (highest priority — intent beats heuristics)
// ---------------------------------------------------------------------------
const NAME_PATTERNS = [
  { re: /\b(threshold|lact[ae]te|LT\b|comfortably\s*hard)\b/i, ec: EFFORT_CLASSES.THRESHOLD, insight: "Detected threshold effort from activity name." },
  { re: /\b(interval|repeats|repetition|vo2|vo₂)\b|\d+\s*x\s*\d+/i,  ec: EFFORT_CLASSES.INTERVALS, insight: "Detected structured intervals from activity name." },
  { re: /\b(tempo\s*run|tempo)\b/i,                                   ec: EFFORT_CLASSES.TEMPO,     insight: "Detected tempo effort from activity name." },
  { re: /\b(parkrun|park\s*run|race|competition|compet|marathon|half\s*marathon)\b/i, ec: EFFORT_CLASSES.RACE, insight: "Detected competitive effort from activity name." },
  { re: /\b(hill\s*rep|hill\s*sprint|climb\s*rep|colina|subida)\b/i, ec: EFFORT_CLASSES.HILLS,     insight: "Detected hill repeats from activity name." },
  { re: /\b(speed\s*work|sprint|strides|accelerat|neuromuscular)\b/i, ec: EFFORT_CLASSES.SPEED,    insight: "Detected speed/sprint session from activity name." },
  { re: /\b(fartlek|speed\s*play)\b/i,                                ec: EFFORT_CLASSES.FARTLEK,  insight: "Detected fartlek session from activity name." },
  { re: /\b(long\s*run|lsd|long\s*slow|endurance\s*run)\b/i,         ec: EFFORT_CLASSES.LONG_RUN, insight: "Detected long run from activity name." },
  { re: /\b(recovery\s*run|easy\s*run|recovery\s*jog|regenerat)\b/i, ec: EFFORT_CLASSES.RECOVERY, insight: "Detected recovery run from activity name." },
];

// ---------------------------------------------------------------------------
// Field resolvers
// ---------------------------------------------------------------------------
function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function normalizeSport(v) { return String(v || "").trim().toUpperCase(); }

function resolveDistanceKm(a) {
  const d = num(a?.distanceKm);
  if (d != null) return d;
  const m = num(a?.distanceMeters);
  return m != null ? m / 1000 : null;
}

function resolveMovingSec(a) {
  return num(a?.movingTimeSec) ?? num(a?.moving_time_sec) ?? num(a?.movingTime);
}

function resolveElevationM(a) {
  return num(a?.elevationM)
    ?? num(a?.totalElevationGain)
    ?? num(a?.total_elevation_gain)
    ?? num(a?.elevation)
    ?? num(a?.elevationMeters)
    ?? num(a?.totalElevationM);
}

function resolveName(a) {
  return String(a?.name || a?.title || a?.activityName || "").trim();
}

function resolveSpeedKmh(a) {
  const s = num(a?.averageSpeed);
  if (s != null) return s * 3.6;
  return num(a?.avgSpeed);
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------
function epk(elevM, distKm) {
  return elevM != null && distKm != null && distKm > 0 ? elevM / distKm : 0;
}

function terrain(elevPerKm) {
  if (elevPerKm >= T.EPK_MOUNTAIN) return "MOUNTAIN";
  if (elevPerKm >= T.EPK_HILLY)   return "HILLY";
  if (elevPerKm >= T.EPK_ROLLING) return "ROLLING";
  return "ROAD";
}

function formatDuration(sec) {
  if (!Number.isFinite(sec) || sec <= 0) return null;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}

// ---------------------------------------------------------------------------
// Intensity (road only — pace + HR)
// ---------------------------------------------------------------------------
function roadIntensity(distKm, movSec, hr) {
  const pace = distKm != null && movSec != null && distKm > 0 ? movSec / distKm : null;

  if (pace == null) return "EASY";
  if (pace < 280) return "VERY_HARD";
  if (pace <= 300) return "HARD";
  if (pace <= 360) return "STEADY";
  if (pace > 390) {
    const shortRun = distKm == null || distKm < 8;
    const shortDur = movSec == null || movSec < 3600;
    if (shortRun || shortDur) return "RECOVERY";
  }
  return "EASY";
}

function intensityClassKey(intensity) {
  return String(intensity || EFFORT_CLASSES.EASY).trim().toLowerCase();
}

function deriveRoadTypeClass(distKm) {
  const d = distKm ?? 0;
  if (d > T.ROAD_EXTREME_MIN) return EFFORT_CLASSES.EXTREME;
  if (d > T.ROAD_ULTRA_MIN)   return EFFORT_CLASSES.ULTRA;
  if (d >= T.ROAD_MARA_MIN && d <= T.ROAD_MARA_MAX) return EFFORT_CLASSES.MARATHON;
  if (d >= T.ROAD_LONG_MIN && d < T.ROAD_LONG_MAX)  return EFFORT_CLASSES.LONG_RUN;
  if (d >= T.ROAD_HALF_MIN && d < T.ROAD_HALF_MAX)  return EFFORT_CLASSES.HALF_MARATHON;
  if (d >= T.ROAD_MED_MIN && d < T.ROAD_MED_MAX)    return EFFORT_CLASSES.MEDIUM;
  if (d >= T.ROAD_10K_MIN && d < T.ROAD_10K_MAX)    return EFFORT_CLASSES.TEN_K;
  if (d >= T.ROAD_5K_MIN && d < T.ROAD_5K_MAX)      return EFFORT_CLASSES.FIVE_K;
  return EFFORT_CLASSES.SHORT;
}

function deriveActivityTypeClass(activity, fallbackClass) {
  const name = resolveName(activity);
  const sport = normalizeSport(activity?.sportType || activity?.sport);
  const distKm = resolveDistanceKm(activity);
  const elevM = resolveElevationM(activity);
  const e_pk = epk(elevM, distKm);
  const isStravaTrail = sport === "TRAILRUN" || sport === "TRAIL_RUN";
  const isTrailByName = /\b(trail|trilho|ultra|utmb|miut|montanha|mountain|skyrace)\b/i.test(name);
  const isTrailByElev = e_pk >= T.EPK_HILLY;

  if (TRAIL_TYPE_CLASSES.has(fallbackClass)) return fallbackClass;

  if (distKm != null && (sport.includes("RUN") || isStravaTrail) && !(isStravaTrail || isTrailByName || isTrailByElev)) {
    return deriveRoadTypeClass(distKm);
  }
  return fallbackClass || EFFORT_CLASSES.BASE_RUN;
}

function deriveActivityIntensityClass(activity, fallbackClass) {
  const backend = parseResult(activity?.classificationResult);
  const explanation = String(backend?.explanation || "").toLowerCase();
  if (explanation.includes("athlete baseline")) {
    return fallbackClass || EFFORT_CLASSES.EASY;
  }

  const name = resolveName(activity);
  const sport = normalizeSport(activity?.sportType || activity?.sport);
  const distKm = resolveDistanceKm(activity);
  const movSec = resolveMovingSec(activity);
  const elevM = resolveElevationM(activity);
  const e_pk = epk(elevM, distKm);
  const isStravaTrail = sport === "TRAILRUN" || sport === "TRAIL_RUN";
  const isTrailByName = /\b(trail|trilho|ultra|utmb|miut|montanha|mountain|skyrace)\b/i.test(name);
  const isTrailByElev = e_pk >= T.EPK_HILLY;

  if (distKm != null && movSec != null && (sport.includes("RUN") || isStravaTrail)) {
    if (isStravaTrail || isTrailByName || isTrailByElev) return intensityClassKey(trailIntensity(distKm, movSec, elevM));
    return intensityClassKey(roadIntensity(distKm, movSec));
  }
  return fallbackClass || EFFORT_CLASSES.EASY;
}

// Trail intensity thresholds — grade-adjusted pace (ITRA model):
//   equiv_dist = distKm + elevM/100 ;  equiv_pace = movSec / equiv_dist
const TRAIL_PACE_VERY_HARD = 300;  // s/km equiv (< 5:00/km flat)
const TRAIL_PACE_HARD      = 360;  // s/km equiv (< 6:00/km flat)
const TRAIL_PACE_STEADY    = 420;  // s/km equiv (< 7:00/km flat)

function trailIntensity(distKm, movSec, elevM) {
  if (!distKm || distKm <= 0 || !movSec || movSec <= 0) return "EASY";
  const equivDist = distKm + (elevM ?? 0) / 100;
  if (equivDist <= 0) return "EASY";
  const equivPace = movSec / equivDist;
  if (equivPace < TRAIL_PACE_VERY_HARD) return "VERY_HARD";
  if (equivPace <= TRAIL_PACE_HARD)     return "HARD";
  if (equivPace <= TRAIL_PACE_STEADY)   return "STEADY";
  return "EASY";
}

function intensityToEffortClass(intensity) {
  if (intensity === "RECOVERY")  return EFFORT_CLASSES.RECOVERY;
  if (intensity === "VERY_HARD") return EFFORT_CLASSES.TEMPO;
  if (intensity === "HARD")      return EFFORT_CLASSES.STEADY;
  return EFFORT_CLASSES.BASE_RUN;
}

// ---------------------------------------------------------------------------
// Trail classification — 8-tier priority chain
// ---------------------------------------------------------------------------
function classifyTrail(distKm, elevM, e_pk, movSec, hr, isStravaTrail) {
  const d  = distKm ?? 0;
  const el = elevM  ?? 0;
  const dur = formatDuration(movSec);
  const intensityClass = intensityClassKey(trailIntensity(distKm, movSec, elevM));

  function trailResult(effortClass, autoInsight, classificationFlag = null) {
    return { effortClass, typeClass: effortClass, intensityClass, autoInsight, classificationFlag };
  }

  // 1. Ultra Endurance
  if (d >= T.UE_MIN_KM && el >= T.UE_MIN_ELEV && e_pk >= T.UE_MIN_EPK) {
    return trailResult(EFFORT_CLASSES.ULTRA_ENDURANCE, `Ultra Endurance: ${d.toFixed(0)} km / ${el.toFixed(0)} m D+ (${e_pk.toFixed(0)} m/km).`);
  }

  // 2. Extreme Ultra — complement of UE (fix C1)
  if (d >= T.UE_MIN_KM) {
    return trailResult(EFFORT_CLASSES.EXTREME_ULTRA, `Extreme Ultra: ${d.toFixed(0)} km / ${el.toFixed(0)} m D+.`);
  }

  // 3. VK: < 8 km AND D+ ≥ 1000 m AND D+/km > 100 (fix M1 — upper bound < 8 km)
  if (d < T.VK_MAX_KM && el >= T.VK_MIN_ELEV && e_pk > T.VK_MIN_EPK) {
    return trailResult(EFFORT_CLASSES.VK, `Vertical KM: ${d.toFixed(1)} km / ${el.toFixed(0)} m D+ (${e_pk.toFixed(0)} m/km).`);
  }

  // 4. Skyrace: 20–42 km, D+ ≥ 2000 m, D+/km ≥ 70 (fix M4 — upper = 42 km)
  if (d >= T.SKY_MIN_KM && d <= T.SKY_MAX_KM && el >= T.SKY_MIN_ELEV && e_pk >= T.SKY_MIN_EPK) {
    return trailResult(EFFORT_CLASSES.SKYRACE, `Skyrace: ${d.toFixed(0)} km / ${el.toFixed(0)} m D+ (${e_pk.toFixed(0)} m/km).`);
  }

  // 5. Ultra Trail: > 42 km
  if (d > T.ULTRA_MIN_KM) {
    const isMtn = el >= T.ULTRA_MTN_ELEV || e_pk >= T.ULTRA_MTN_EPK;
    const sub   = isMtn ? "Mountain" : "Plain";
    return trailResult(EFFORT_CLASSES.ULTRA_TRAIL, `Ultra Trail (${sub}): ${d.toFixed(0)} km / ${el.toFixed(0)} m D+${dur ? ` in ${dur}` : ""}.`);
  }

  // 6. Long Trail (Skyrace already checked — has priority, fix M2)
  const longByDist = d >= T.LONG_MIN_KM;
  const longByElev = el >= T.LONG_ELEV_M && d >= T.LONG_ELEV_MIN_KM;  // fix N2
  const longByComb = d >= T.LONG_COMB_KM && el >= T.LONG_COMB_ELEV;
  if (longByDist || longByElev || longByComb) {
    return trailResult(EFFORT_CLASSES.LONG_TRAIL, `Long trail: ${d.toFixed(0)} km / ${el.toFixed(0)} m D+ (${e_pk.toFixed(0)} m/km).`);
  }

  // 7. Trail Run: ≥ 8 km AND elevation gate met
  if (d >= T.TRAIL_MIN_KM && (el >= T.TRAIL_GATE_ELEV || e_pk >= T.TRAIL_GATE_EPK)) {
    return trailResult(EFFORT_CLASSES.TRAIL_RUN, `Trail run: ${d.toFixed(1)} km / ${el.toFixed(0)} m D+ (${e_pk.toFixed(0)} m/km).`);
  }

  // 8. Short Trail / flat Strava TrailRun
  const flag = isStravaTrail && d >= T.TRAIL_MIN_KM && el < T.TRAIL_GATE_ELEV && e_pk < T.TRAIL_GATE_EPK
    ? T.FLAG_LOW_ELEV
    : null;
  return trailResult(EFFORT_CLASSES.SHORT_TRAIL, `Short/flat trail: ${d.toFixed(1)} km / ${el.toFixed(0)} m D+.`, flag);
}

// ---------------------------------------------------------------------------
// Road classification — 9-tier distance-only (no time, fix C2)
// ---------------------------------------------------------------------------
function classifyRoad(distKm, movSec, hr) {
  const d = distKm ?? 0;
  const intensity = roadIntensity(distKm, movSec, hr);
  const intensityClass = intensityClassKey(intensity);
  const dur = formatDuration(movSec);
  const distStr = distKm != null ? `${distKm.toFixed(1)} km` : "-";

  function withIntensity(ec) {
    if (NAMED_ROAD_TIERS.has(ec)) return { effortClass: ec, typeClass: ec, intensityClass };
    const override = intensityToEffortClass(intensity);
    return { effortClass: override, typeClass: ec, intensityClass, autoInsight: `${override.replace(/_/g, " ")} road run: ${distStr}${dur ? ` in ${dur}` : ""}.` };
  }

  if (d > T.ROAD_EXTREME_MIN) return { effortClass: EFFORT_CLASSES.EXTREME,       typeClass: EFFORT_CLASSES.EXTREME,       intensityClass, autoInsight: `Extreme road run: ${distStr}.` };
  if (d > T.ROAD_ULTRA_MIN)   return { effortClass: EFFORT_CLASSES.ULTRA,         typeClass: EFFORT_CLASSES.ULTRA,         intensityClass, autoInsight: `Ultra road run: ${distStr}.` };
  if (d >= T.ROAD_MARA_MIN && d <= T.ROAD_MARA_MAX) return { effortClass: EFFORT_CLASSES.MARATHON,      typeClass: EFFORT_CLASSES.MARATHON,      intensityClass, autoInsight: `Marathon distance: ${distStr}.` };
  if (d >= T.ROAD_LONG_MIN && d < T.ROAD_LONG_MAX)  return withIntensity(EFFORT_CLASSES.LONG_RUN);
  if (d >= T.ROAD_HALF_MIN && d < T.ROAD_HALF_MAX)  return { effortClass: EFFORT_CLASSES.HALF_MARATHON, typeClass: EFFORT_CLASSES.HALF_MARATHON, intensityClass, autoInsight: `Half Marathon distance: ${distStr}.` };
  if (d >= T.ROAD_MED_MIN && d < T.ROAD_MED_MAX)    return withIntensity(EFFORT_CLASSES.MEDIUM);
  if (d >= T.ROAD_10K_MIN && d < T.ROAD_10K_MAX)    return { effortClass: EFFORT_CLASSES.TEN_K,         typeClass: EFFORT_CLASSES.TEN_K,         intensityClass, autoInsight: `10K distance: ${distStr}.` };
  if (d >= T.ROAD_5K_MIN && d < T.ROAD_5K_MAX)      return { effortClass: EFFORT_CLASSES.FIVE_K,        typeClass: EFFORT_CLASSES.FIVE_K,        intensityClass, autoInsight: `5K distance: ${distStr}.` };

  // Short — apply intensity
  return withIntensity(EFFORT_CLASSES.SHORT);
}

// ---------------------------------------------------------------------------
// Non-run classifiers
// ---------------------------------------------------------------------------
function classifyRide(distKm, movSec, speedKmh, hr) {
  if ((distKm != null && distKm >= 60) || (movSec != null && movSec >= 7200)) {
    return { effortClass: EFFORT_CLASSES.LONG_RUN, typeClass: EFFORT_CLASSES.LONG_RUN, intensityClass: EFFORT_CLASSES.EASY, autoInsight: `Long ride: ${distKm?.toFixed(1) ?? "-"} km.` };
  }
  if ((speedKmh != null && speedKmh >= 28) || (hr != null && hr >= 145)) {
    return { effortClass: EFFORT_CLASSES.TEMPO, typeClass: EFFORT_CLASSES.BASE_RUN, intensityClass: EFFORT_CLASSES.TEMPO, autoInsight: `Tempo ride${speedKmh != null ? ` at ${speedKmh.toFixed(1)} km/h` : ""}.` };
  }
  return { effortClass: EFFORT_CLASSES.BASE_RUN, typeClass: EFFORT_CLASSES.BASE_RUN, intensityClass: EFFORT_CLASSES.EASY, autoInsight: "Aerobic ride for conditioning." };
}

function classifyHike(distKm, movSec) {
  const isUltra = (distKm != null && distKm >= 42) || (movSec != null && movSec >= 18000);
  const isLong  = (distKm != null && distKm >= 12) || (movSec != null && movSec >= 7200);
  if (isUltra) return { effortClass: EFFORT_CLASSES.ULTRA_TRAIL, typeClass: EFFORT_CLASSES.ULTRA_TRAIL, intensityClass: EFFORT_CLASSES.STEADY, autoInsight: "Ultra outing on feet." };
  if (isLong)  return { effortClass: EFFORT_CLASSES.LONG_TRAIL,  typeClass: EFFORT_CLASSES.LONG_TRAIL,  intensityClass: EFFORT_CLASSES.STEADY, autoInsight: `Long hike/trail outing: ${distKm?.toFixed(1) ?? "-"} km.` };
  return { effortClass: EFFORT_CLASSES.BASE_RUN, typeClass: EFFORT_CLASSES.BASE_RUN, intensityClass: EFFORT_CLASSES.EASY, autoInsight: "Easy effort focused on time on feet." };
}

// ---------------------------------------------------------------------------
// Public API — classify a single activity object
// ---------------------------------------------------------------------------

/**
 * Classify an activity. Returns { effortClass, autoInsight, classificationFlag? }.
 *
 * Step 1: name-based keyword detection (highest priority)
 * Step 2: sport-family dispatch → trail or road path
 * Step 3: intensity override on non-named road tiers
 */
export function classifyActivity(activity) {
  // Step 1: name-based detection
  const name = resolveName(activity);
  if (name) {
    for (const { re, ec, insight } of NAME_PATTERNS) {
      if (re.test(name)) {
        return {
          effortClass: ec,
          typeClass: deriveActivityTypeClass(activity, ec),
          intensityClass: ec,
          autoInsight: insight,
        };
      }
    }
  }

  const sport    = normalizeSport(activity?.sportType || activity?.sport);
  const distKm   = resolveDistanceKm(activity);
  const movSec   = resolveMovingSec(activity);
  const elevM    = resolveElevationM(activity);
  const hr       = num(activity?.averageHeartrate ?? activity?.avgHR);
  const speedKmh = resolveSpeedKmh(activity);

  if (distKm != null && distKm <= 0) return { effortClass: EFFORT_CLASSES.UNKNOWN, autoInsight: "Invalid distance value." };
  if (movSec != null && movSec <= 0) return { effortClass: EFFORT_CLASSES.UNKNOWN, autoInsight: "Invalid moving time value." };

  const e_pk = epk(elevM, distKm);
  const isStravaTrail = sport === "TRAILRUN" || sport === "TRAIL_RUN";
  const isTrailByName = /\b(trail|trilho|ultra|utmb|miut|montanha|mountain|skyrace)\b/i.test(name);
  const isTrailByElev = e_pk >= T.EPK_HILLY;

  if (sport.includes("RUN") || isStravaTrail) {
    const isTrail = isStravaTrail || isTrailByName || isTrailByElev;
    if (isTrail) return classifyTrail(distKm, elevM, e_pk, movSec, hr, isStravaTrail);
    return classifyRoad(distKm, movSec, hr);
  }

  if (sport.includes("RIDE") || sport.includes("BIKE") || sport === "CYCLING") {
    return classifyRide(distKm, movSec, speedKmh, hr);
  }

  if (sport.includes("HIKE") || sport.includes("WALK")) {
    return classifyHike(distKm, movSec);
  }

  if (movSec != null && movSec >= 7200) return { effortClass: EFFORT_CLASSES.LONG_RUN, typeClass: EFFORT_CLASSES.LONG_RUN, intensityClass: EFFORT_CLASSES.EASY, autoInsight: "Long duration session." };
  return { effortClass: EFFORT_CLASSES.BASE_RUN, typeClass: EFFORT_CLASSES.BASE_RUN, intensityClass: EFFORT_CLASSES.EASY, autoInsight: "Easy session." };
}

// ---------------------------------------------------------------------------
// Backend result resolver — prefers backend classification, falls back to JS
// ---------------------------------------------------------------------------
function parseResult(v) {
  if (!v) return null;
  if (typeof v === "object") return v;
  try { return JSON.parse(v); } catch { return null; }
}

export function resolveActivityClassification(activity) {
  const backend = parseResult(activity?.classificationResult);
  const pt = String(backend?.primaryType || "").trim().toUpperCase();
  if (pt) {
    const primaryClass = BACKEND_PRIMARY_TYPE_TO_EFFORT_CLASS[pt] || EFFORT_CLASSES.UNKNOWN;
    const intensityClass = deriveActivityIntensityClass(activity, intensityClassKey(backend?.intensity));
    return {
      effortClass: primaryClass,
      typeClass: deriveActivityTypeClass(activity, primaryClass),
      intensityClass,
      autoInsight: backend?.explanation || "Backend activity classification",
      classificationFlag: backend?.classificationFlag ?? null,
      source: "backend",
      backendClassification: backend,
    };
  }
  return { ...classifyActivity(activity), source: "frontend", backendClassification: null };
}

// ---------------------------------------------------------------------------
// Display labels
// ---------------------------------------------------------------------------
const EFFORT_LABELS = {
  [EFFORT_CLASSES.ULTRA_ENDURANCE]: "Ultra Endurance",
  [EFFORT_CLASSES.EXTREME_ULTRA]:   "Extreme Ultra",
  [EFFORT_CLASSES.VK]:              "Vertical KM",
  [EFFORT_CLASSES.SKYRACE]:         "Skyrace",
  [EFFORT_CLASSES.ULTRA_TRAIL]:     "Ultra Trail",
  [EFFORT_CLASSES.LONG_TRAIL]:      "Long Trail",
  [EFFORT_CLASSES.TRAIL_RUN]:       "Trail",
  [EFFORT_CLASSES.SHORT_TRAIL]:     "Short Trail",
  [EFFORT_CLASSES.EXTREME]:         "Extreme",
  [EFFORT_CLASSES.ULTRA]:           "Ultra",
  [EFFORT_CLASSES.MARATHON]:        "Marathon",
  [EFFORT_CLASSES.LONG_RUN]:        "Long",
  [EFFORT_CLASSES.HALF_MARATHON]:   "Half Marathon",
  [EFFORT_CLASSES.MEDIUM]:          "Medium",
  [EFFORT_CLASSES.TEN_K]:           "10K",
  [EFFORT_CLASSES.FIVE_K]:          "5K",
  [EFFORT_CLASSES.SHORT]:           "Short",
  [EFFORT_CLASSES.BASE_RUN]:        "Base Run",
  [EFFORT_CLASSES.RECOVERY]:        "Recovery",
  [EFFORT_CLASSES.EASY]:            "Easy",
  hard:                             "Hard",
  very_hard:                        "Very Hard",
  [EFFORT_CLASSES.STEADY]:          "Steady",
  [EFFORT_CLASSES.TEMPO]:           "Tempo",
  [EFFORT_CLASSES.THRESHOLD]:       "Threshold",
  [EFFORT_CLASSES.INTERVALS]:       "Intervals",
  [EFFORT_CLASSES.HILLS]:           "Hills",
  [EFFORT_CLASSES.SPEED]:           "Speed",
  [EFFORT_CLASSES.FARTLEK]:         "Fartlek",
  [EFFORT_CLASSES.RACE]:            "Race",
  [EFFORT_CLASSES.UNKNOWN]:         "Unknown",
};

export function formatEffortClassLabel(effortClass) {
  const key = String(effortClass || "").trim().toLowerCase();
  return EFFORT_LABELS[key] ?? (key ? key.charAt(0).toUpperCase() + key.slice(1) : "Easy");
}
